import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Payment as PaymentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarMonth as CalendarIcon,
  CameraAlt as CameraIcon,
} from '@mui/icons-material';
import ProfessionalAvatar from '../components/ProfessionalAvatar';
import * as MuiIcons from '@mui/icons-material';
import professionalsService from '../services/professionalsService';
import photoService from '../services/photoService';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { useContextualHint } from '../hooks/useContextualHint';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import ContextualHint from '../components/ContextualHint';
import ProfessionalForm from '../components/forms/ProfessionalForm';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import { getInitials } from '../utils/professionalNameUtils';
import { ProfessionalsDataProvider, useProfessionalsData } from '../contexts/ProfessionalsDataContext';

// Helper para obter ícone dinamicamente
const getIconComponent = (iconName) => {
  const IconComponent = MuiIcons[iconName];
  return IconComponent || MuiIcons.HelpOutline;
};

export default function ProfessionalsPage() {
  const [normalizedData, setNormalizedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const photoDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar} = useSnackbar();
  const { getIcon } = useThemeCustom();
  
  // Estado local apenas para expansão de cards (não afeta CRUD)
  const [expandedCards, setExpandedCards] = useState({});
  const [selectedProfessional, setSelectedProfessional] = useState(null);

  // Cache de timestamps de fotos (persiste no localStorage)
  const PHOTO_TIMESTAMPS_KEY = 'professional_photo_timestamps';
  
  const getPhotoTimestamps = () => {
    try {
      const stored = localStorage.getItem(PHOTO_TIMESTAMPS_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  };
  
  const savePhotoTimestamp = (professionalId, timestamp) => {
    const timestamps = getPhotoTimestamps();
    timestamps[professionalId] = timestamp;
    localStorage.setItem(PHOTO_TIMESTAMPS_KEY, JSON.stringify(timestamps));
  };

  // Obter dica contextual
  const professionals = normalizedData?.professionals || [];
  const hint = useContextualHint('professionals', { items: professionals });

  // Carregar dados normalizados
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await professionalsService.getAll();
      console.log('🔍 [ProfessionalsPage] Dados da API:', data);
      console.log('🔍 [ProfessionalsPage] Primeiro profissional:', data?.professionals?.[0]);
      
      // Adicionar photoTimestamps do localStorage aos profissionais
      const timestamps = getPhotoTimestamps();
      const professionalsWithTimestamps = data.professionals.map(p => ({
        ...p,
        photoTimestamp: timestamps[p.id] || null
      }));
      
      setNormalizedData({
        ...data,
        professionals: professionalsWithTimestamps
      });
    } catch (err) {
      console.error('Erro ao carregar profissionais:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handlers CRUD
  const handleCreate = async (data) => {
    try {
      await professionalsService.create(data);
      formDialog.handleClose();
      showSuccess('Profissional criado com sucesso!');
      loadData(); // Recarregar dados normalizados
    } catch (err) {
      showError('Erro ao criar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (data) => {
    try {
      await professionalsService.update(formDialog.data.id, data);
      formDialog.handleClose();
      showSuccess('Profissional atualizado com sucesso!');
      loadData(); // Recarregar dados normalizados
    } catch (err) {
      showError('Erro ao atualizar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  const handleDelete = async () => {
    try {
      await professionalsService.delete(deleteDialog.data.id);
      deleteDialog.handleClose();
      showSuccess('Profissional excluído com sucesso!');
      loadData(); // Recarregar dados normalizados
    } catch (err) {
      showError('Erro ao excluir: ' + (err.response?.data?.message || err.message));
    }
  };

  const toggleCardExpansion = (professionalId) => {
    setExpandedCards(prev => ({
      ...prev,
      [professionalId]: !prev[professionalId]
    }));
  };

  // ✅ Handlers de Foto
  const handlePhotoClick = (professional) => {
    setSelectedProfessional(professional);
    photoDialog.handleOpen();
  };

  const handlePhotoUpload = async (file) => {
    try {
      if (!selectedProfessional?.id) {
        showError('Profissional não selecionado');
        return;
      }
      await photoService.uploadProfilePhoto(file, selectedProfessional.id);
      
      // Salvar timestamp no localStorage para persistência
      const timestamp = Date.now();
      savePhotoTimestamp(selectedProfessional.id, timestamp);
      
      // Atualizar timestamp da foto no estado local para forçar reload da imagem (cache busting)
      setNormalizedData(prev => ({
        ...prev,
        professionals: prev.professionals.map(p => 
          p.id === selectedProfessional.id 
            ? { ...p, photoTimestamp: timestamp }
            : p
        )
      }));
      
      photoDialog.handleClose();
      showSuccess('Foto atualizada com sucesso!');
      
      // Recarregar profissionais após upload
      await loadData();
    } catch (err) {
      showError('Erro ao fazer upload: ' + (err.response?.data?.message || err.message));
    }
  };

  // ✅ Loading/Error States
  if (loading) return <LoadingState message="Carregando profissionais..." />;
  if (error) return <ErrorState error={error} onRetry={loadData} />;
  if (!normalizedData) return null;

  return (
    <ProfessionalsDataProvider data={normalizedData}>
      <ProfessionalsPageContent
        professionals={normalizedData.professionals}
        hint={hint}
        formDialog={formDialog}
        deleteDialog={deleteDialog}
        photoDialog={photoDialog}
        selectedProfessional={selectedProfessional}
        snackbar={snackbar}
        hideSnackbar={hideSnackbar}
        handleSubmit={handleSubmit}
        handleDelete={handleDelete}
        handlePhotoClick={handlePhotoClick}
        handlePhotoUpload={handlePhotoUpload}
        expandedCards={expandedCards}
        toggleCardExpansion={toggleCardExpansion}
      />
    </ProfessionalsDataProvider>
  );
}

// Componente interno que tem acesso ao context
function ProfessionalsPageContent({
  professionals,
  hint,
  formDialog,
  deleteDialog,
  photoDialog,
  selectedProfessional,
  snackbar,
  hideSnackbar,
  handleSubmit,
  handleDelete,
  handlePhotoClick,
  handlePhotoUpload,
  expandedCards,
  toggleCardExpansion,
}) {
  const { getPlace, getService, getProfessionalStats } = useProfessionalsData();
  const { getIcon } = useThemeCustom();

  // ✅ Formatters
  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return `R$ ${parseFloat(value).toFixed(2).replace('.', ',')}`;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}min`;
    if (hours > 0) return `${hours}h`;
    return `${mins}min`;
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            <PersonIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32 }} />
            Profissionais
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gerencie os profissionais, seus serviços e vínculos com locais
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => formDialog.handleOpen(null)}>
          Novo Profissional
        </Button>
      </Box>

      {/* Dica Contextual */}
      <ContextualHint hint={hint} onActionClick={() => formDialog.handleOpen(null)} />

      {/* Empty State */}
      {professionals.length === 0 ? (
        <EmptyState
          icon={PersonIcon}
          title="Nenhum profissional cadastrado"
          message="Comece criando seu primeiro profissional para gerenciar serviços e agendamentos"
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => formDialog.handleOpen(null)}>
              Criar Primeiro Profissional
            </Button>
          }
        />
      ) : (
        <Grid container spacing={3}>
          {professionals.map((professional) => {
            const isExpanded = expandedCards[professional.id];
            const stats = getProfessionalStats(professional);
            
            return (
              <Grid item xs={12} md={6} lg={4} key={professional.id}>
                <Card elevation={2} sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  overflow: 'hidden',
                  maxWidth: '100%'
                }}>
                  {/* Header do Card com Avatar */}
                  <Box sx={{ 
                    p: 2, 
                    pb: 1, 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 2,
                    minWidth: 0
                  }}>
                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                      <ProfessionalAvatar
                        professionalId={professional.id}
                        photoTimestamp={professional.photoTimestamp}
                        name={professional.name}
                        sx={{
                          width: { xs: 56, sm: 64 },
                          height: { xs: 56, sm: 64 },
                          bgcolor: 'primary.main',
                          fontSize: { xs: 20, sm: 24 },
                          fontWeight: 'bold'
                        }}
                      >
                        {getInitials(professional.name)}
                      </ProfessionalAvatar>
                      <IconButton
                        size="small"
                        onClick={() => handlePhotoClick(professional)}
                        sx={{
                          position: 'absolute',
                          bottom: -4,
                          right: -4,
                          bgcolor: 'primary.main',
                          color: 'white',
                          width: 28,
                          height: 28,
                          '&:hover': {
                            bgcolor: 'primary.dark'
                          },
                          boxShadow: 2
                        }}
                      >
                        <CameraIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        noWrap 
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                      >
                        {professional.name}
                      </Typography>
                      {professional.specialty && (
                        <Chip 
                          label={professional.specialty} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ 
                            mt: 0.5,
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  <CardContent sx={{ pt: 1, pb: 1, flex: 1, minWidth: 0 }}>
                    {/* Informações de Contato */}
                    <List dense disablePadding>
                      {professional.email && (
                        <ListItem disablePadding sx={{ mb: 0.5, minWidth: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32, flexShrink: 0 }}>
                            <EmailIcon fontSize="small" color="action" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={professional.email} 
                            primaryTypographyProps={{ 
                              variant: 'body2', 
                              noWrap: true,
                              sx: { overflow: 'hidden', textOverflow: 'ellipsis' }
                            }}
                          />
                        </ListItem>
                      )}
                      {professional.phone && (
                        <ListItem disablePadding sx={{ mb: 0.5, minWidth: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32, flexShrink: 0 }}>
                            <PhoneIcon fontSize="small" color="action" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={professional.phone} 
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      )}
                      {professional.registrationNumber && (
                        <ListItem disablePadding sx={{ mb: 0.5, minWidth: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32, flexShrink: 0 }}>
                            <BadgeIcon fontSize="small" color="action" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={professional.registrationNumber} 
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      )}
                    </List>

                    <Divider sx={{ my: 1.5 }} />

                    {/* Estatísticas Rápidas */}
                    <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                      <Chip 
                        icon={React.createElement(getIconComponent(getIcon('places').name))}
                        label={`${stats.placesCount} ${stats.placesCount === 1 ? 'Local' : 'Locais'}`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip 
                        icon={React.createElement(getIconComponent(getIcon('services').name))}
                        label={`${stats.servicesCount} ${stats.servicesCount === 1 ? 'Serviço' : 'Serviços'}`}
                        size="small"
                        variant="outlined"
                        color={stats.servicesCount > 0 ? 'primary' : 'default'}
                      />
                    </Box>

                    {/* Detalhes Expandíveis */}
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box mt={2}>
                        <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                          Serviços por Local:
                        </Typography>
                        {professional.places.length === 0 ? (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            Nenhum serviço vinculado ainda
                          </Alert>
                        ) : (
                          professional.places.map((placeLink) => {
                            const place = getPlace(placeLink.placeId);
                            if (!place) return null;

                            return (
                              <Paper key={placeLink.id} variant="outlined" sx={{ 
                                p: 1.5, 
                                mb: 1,
                                overflow: 'hidden'
                              }}>
                                <Typography 
                                  variant="subtitle2" 
                                  fontWeight="bold" 
                                  gutterBottom
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {React.createElement(getIconComponent(getIcon('places').name), { fontSize: 'small', sx: { flexShrink: 0 } })}
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {place.name}
                                  </span>
                                </Typography>
                                <List dense disablePadding>
                                  {placeLink.services.length === 0 ? (
                                    <ListItem disablePadding>
                                      <ListItemText 
                                        primary="Nenhum serviço configurado neste local"
                                        primaryTypographyProps={{ 
                                          variant: 'body2',
                                          color: 'text.secondary',
                                          fontStyle: 'italic'
                                        }}
                                      />
                                    </ListItem>
                                  ) : (
                                    placeLink.services.map((serviceLink) => {
                                      const service = getService(serviceLink.serviceId);
                                      if (!service) return null;

                                      return (
                                        <ListItem key={serviceLink.id} disablePadding sx={{ pl: { xs: 0, sm: 3 }, mb: 1 }}>
                                          <ListItemText
                                            primary={
                                              <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                                                {React.createElement(getIconComponent(getIcon('services').name), { fontSize: 'small', color: 'action', sx: { flexShrink: 0 } })}
                                                <Typography 
                                                  variant="body2"
                                                  sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                  }}
                                                >
                                                  {service.name}
                                                </Typography>
                                              </Box>
                                            }
                                            secondary={
                                              <Box component="span" display="flex" gap={1} mt={0.5}>
                                                {service.durationMinutes && (
                                                  <Chip
                                                    size="small"
                                                    label={formatDuration(service.durationMinutes)}
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                  />
                                                )}
                                                {service.price && (
                                                  <Chip
                                                    size="small"
                                                    label={formatCurrency(service.price)}
                                                    color="success"
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                  />
                                                )}
                                              </Box>
                                            }
                                          />
                                        </ListItem>
                                      );
                                    })
                                  )}
                                </List>
                              </Paper>
                            );
                          })
                        )}
                      </Box>
                    </Collapse>
                  </CardContent>

                  {/* Actions */}
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      onClick={() => toggleCardExpansion(professional.id)}
                      endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                      {isExpanded ? 'Menos' : 'Mais'} Detalhes
                    </Button>
                    <Box>
                      <IconButton 
                        size="small" 
                        color="primary" 
                        onClick={() => formDialog.handleOpen(professional)}
                        title="Editar"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => deleteDialog.handleOpen(professional)}
                        title="Excluir"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Professional Form Dialog */}
      <ProfessionalForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        data={formDialog.data}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={deleteDialog.handleClose}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o profissional <strong>{deleteDialog.data?.name}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta ação não pode ser desfeita e pode afetar agendamentos vinculados.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={deleteDialog.handleClose}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Profile Photo Upload Dialog */}
      <ProfilePhotoUpload
        open={photoDialog.open}
        onClose={photoDialog.handleClose}
        onUpload={handlePhotoUpload}
        currentPhotoUrl={selectedProfessional?.photoUrl}
      />
    </Box>
  );
}
